import type { DefaultAccountSlot } from '@clockwise/clients/chart-of-accounts';
import type { InvoiceLineAllocation } from '@clockwise/clients/invoices';
import {
  allocateMoney,
  compareMoney,
  type InvoiceLineKind,
  isMoney,
  money,
  subtractMoney,
  sumMoney,
  toMoney,
} from '@clockwise/shared';
import { Check, Plus, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { AccountPicker } from '@/components/custom/account-picker';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useChartOfAccounts } from '@/lib/chart-of-accounts';
import { cn } from '@/lib/utils';

/**
 * Where an invoice line's money is booked — finance-only, never printed.
 *
 * A line holds a list of these: none (no account yet), one (the whole line to
 * that account) or several (a split). `accountId` is null only on a split row
 * someone has added but not yet given an account; such a row cannot be saved
 * (see `allocationsSchema` in the editor), so it never reaches the server.
 */
export type AllocationValue = { accountId: string | null; amount: string };

/** How long "Allocated X of Y" stays after an edit completes a split, then
 *  how long it takes to fade. */
const ALLOCATED_MESSAGE_MS = 2800;
const ALLOCATED_FADE_MS = 700;

/** The form's allocations for one saved line, in position order. */
export function allocationsForLine(
  lineId: string,
  lineAmount: string,
  all: readonly InvoiceLineAllocation[],
): AllocationValue[] {
  const rows = all
    .filter((a) => a.lineItemId === lineId)
    .sort((a, b) => a.position - b.position)
    .map((a) => ({ accountId: a.accountId, amount: a.amount }));
  // A single allocation IS the line; show the line's own amount so a later
  // edit that rescales starts from the right figure.
  if (rows.length === 1 && rows[0]) rows[0].amount = lineAmount;
  return rows;
}

/** The API payload: rows without an account dropped, and a single account sent
 *  without an amount — the server books it for the whole line. */
export function toAllocationInput(
  allocations: readonly AllocationValue[],
): { accountId: string; amount?: string }[] {
  const named = allocations.filter((a): a is { accountId: string; amount: string } =>
    Boolean(a.accountId),
  );
  if (named.length <= 1) return named.map((a) => ({ accountId: a.accountId }));
  return named.map((a) => ({ accountId: a.accountId, amount: a.amount }));
}

/** Whether every row names an account and the rows add up to the line. */
export function isFullyBooked(allocations: readonly AllocationValue[], amount: string): boolean {
  if (allocations.length === 0 || allocations.some((a) => !a.accountId)) return false;
  if (allocations.length === 1) return true;
  return remainingToAllocate(allocations, amount) === '0.00';
}

/** `amount` minus what the rows hold, or null when a figure is not money yet
 *  (a half-typed amount) and there is nothing honest to say. */
export function remainingToAllocate(
  allocations: readonly AllocationValue[],
  amount: string,
): string | null {
  if (!isMoney(amount) || allocations.some((a) => !isMoney(a.amount))) return null;
  return subtractMoney(amount, sumMoney(allocations.map((a) => a.amount)));
}

/** 1-based numbers of the lines that are not fully booked, for the approval
 *  blocker. */
export function unbookedLineNumbers(
  lines: readonly { amount: string; allocations: readonly AllocationValue[] }[],
): number[] {
  return lines.flatMap((l, i) => (isFullyBooked(l.allocations, l.amount) ? [] : [i + 1]));
}

/**
 * Follow a change to the line's amount. One account simply takes the new
 * amount. A split keeps its proportions, recomputed to the cent so the parts
 * still add up exactly. Nothing changes while the new amount is half-typed.
 */
export function rescaleAllocations(
  allocations: readonly AllocationValue[],
  amount: string,
): AllocationValue[] {
  if (!isMoney(amount) || allocations.length === 0) return [...allocations];
  if (allocations.length === 1)
    return [{ ...(allocations[0] as AllocationValue), amount: toMoney(amount) }];
  const weights = allocations.map((a) =>
    isMoney(a.amount) ? money(a.amount).abs().toString() : '0',
  );
  const parts = allocateMoney(amount, weights);
  return allocations.map((a, i) => ({ ...a, amount: parts[i] as string }));
}

/** The split icon: start a split, or add one more account to one. The new row
 *  starts with whatever is left to allocate (0.00 when starting). */
export function addSplitRow(
  allocations: readonly AllocationValue[],
  amount: string,
): AllocationValue[] {
  const whole = isMoney(amount) ? toMoney(amount) : '0.00';
  if (allocations.length === 0) {
    return [
      { accountId: null, amount: whole },
      { accountId: null, amount: '0.00' },
    ];
  }
  if (allocations.length === 1) {
    return [
      { ...(allocations[0] as AllocationValue), amount: whole },
      { accountId: null, amount: '0.00' },
    ];
  }
  const left = remainingToAllocate(allocations, amount);
  return [...allocations, { accountId: null, amount: left ?? '0.00' }];
}

/** Remove one row of a split. Down to one row, that account takes the whole
 *  line again — the line is no longer split. */
export function removeSplitRow(
  allocations: readonly AllocationValue[],
  index: number,
  amount: string,
): AllocationValue[] {
  const next = allocations.filter((_, i) => i !== index);
  if (next.length === 1 && next[0])
    return [{ ...next[0], amount: isMoney(amount) ? toMoney(amount) : next[0].amount }];
  return next;
}

const SLOT_FOR_KIND: Partial<Record<InvoiceLineKind, DefaultAccountSlot>> = {
  service: 'service',
  expense: 'expense',
  discount: 'discount',
};

/** The invoice-settings default account for a line kind, or null when there is
 *  none. Read off the chart of accounts, which says which defaults name each
 *  account — so the editor needs no access to invoice settings themselves. */
export function useDefaultAccountForKind(): (kind: InvoiceLineKind) => string | null {
  const { accounts } = useChartOfAccounts();
  return (kind) => {
    const slot = SLOT_FOR_KIND[kind];
    if (!slot) return null;
    return accounts.find((a) => a.active && a.defaultFor.includes(slot))?.id ?? null;
  };
}

/**
 * The Account cell of a line. One or no account: a picker. A split: a label
 * saying so, with a tick once it adds up — the accounts themselves are listed
 * in the rows under the line ({@link LineSplitRows}).
 */
export function LineAccountCell({
  lineNo,
  allocations,
  amount,
  editable,
  onChange,
}: {
  lineNo: number;
  allocations: readonly AllocationValue[];
  amount: string;
  editable: boolean;
  onChange: (next: AllocationValue[]) => void;
}) {
  if (allocations.length >= 2) {
    const booked = isFullyBooked(allocations, amount);
    return (
      <div
        className={cn(
          'flex h-9 items-center justify-between gap-1.5 whitespace-nowrap rounded-md border px-2.5 font-medium text-sm',
          'border-primary/40 bg-primary/5',
        )}
      >
        <span>Split across {allocations.length} accounts</span>
        {booked ? (
          <span className="text-emerald-600 dark:text-emerald-400" title="Fully allocated">
            <Check className="size-4" aria-label="Fully allocated" />
          </span>
        ) : null}
      </div>
    );
  }
  return (
    <AccountPicker
      aria-label={`Line ${lineNo} account`}
      value={allocations[0]?.accountId ?? null}
      disabled={!editable}
      onChange={(accountId) =>
        onChange(
          accountId ? [{ accountId, amount: isMoney(amount) ? toMoney(amount) : amount }] : [],
        )
      }
    />
  );
}

/**
 * The accounts of a split line, as rows directly under it — each amount under
 * the Amount column and each account under the Account column, so a split is
 * read without opening anything.
 *
 * Below them: "+ Add account", and the running tally. When the rows do not add
 * up the tally stays, amber, until they do. When an edit makes them add up,
 * "Allocated X of Y" shows for a few seconds and fades; the tick on the line
 * is what remains.
 *
 * `leadingColumns` is how many columns sit before the Amount column, so the
 * rows line up with whichever grid hosts them.
 */
export function LineSplitRows({
  lineNo,
  allocations,
  amount,
  editable,
  leadingColumns,
  errors,
  onChange,
}: {
  lineNo: number;
  allocations: readonly AllocationValue[];
  amount: string;
  editable: boolean;
  leadingColumns: number;
  errors?: readonly (
    | { accountId?: { message?: string }; amount?: { message?: string } }
    | undefined
  )[];
  onChange: (next: AllocationValue[]) => void;
}) {
  const remaining = remainingToAllocate(allocations, amount);
  const balanced = remaining === '0.00';
  const complete = balanced && allocations.every((a) => a.accountId);
  const flash = useCompletedFlash(complete, JSON.stringify([allocations, amount]));
  const used = new Set(allocations.map((a) => a.accountId).filter((id): id is string => !!id));

  const setRow = (i: number, patch: Partial<AllocationValue>) =>
    onChange(allocations.map((a, k) => (k === i ? { ...a, ...patch } : a)));

  return (
    <>
      {allocations.map((a, i) => {
        const err = errors?.[i];
        const others = new Set([...used].filter((id) => id !== a.accountId));
        return (
          <tr key={i} className="bg-primary/[0.03]">
            <td colSpan={leadingColumns} />
            <td className="px-1 py-1">
              <Input
                inputMode="decimal"
                disabled={!editable}
                aria-label={`Line ${lineNo} split ${i + 1} amount`}
                aria-invalid={Boolean(err?.amount)}
                className="text-right tabular-nums"
                value={a.amount}
                onChange={(e) => setRow(i, { amount: e.target.value })}
              />
              {err?.amount?.message ? (
                <p className="mt-1 text-destructive text-xs">{err.amount.message}</p>
              ) : null}
            </td>
            <td className="px-1 py-1">
              <AccountPicker
                aria-label={`Line ${lineNo} split ${i + 1} account`}
                value={a.accountId}
                disabled={!editable}
                hasError={Boolean(err?.accountId)}
                exclude={others}
                onChange={(accountId) => setRow(i, { accountId })}
              />
              {err?.accountId?.message ? (
                <p className="mt-1 text-destructive text-xs">{err.accountId.message}</p>
              ) : null}
            </td>
            <td className="py-1 text-right">
              {editable ? (
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label={`Remove account ${i + 1} from line ${lineNo}`}
                  title="Remove this account"
                  onClick={() => onChange(removeSplitRow(allocations, i, amount))}
                >
                  <X className="size-4" />
                </Button>
              ) : null}
            </td>
          </tr>
        );
      })}
      <tr className="border-b bg-primary/[0.03]">
        <td colSpan={leadingColumns + 1} className="px-1 pt-1 pb-2 text-right">
          {!balanced && remaining !== null ? (
            <span
              role="status"
              className="inline-flex items-center gap-2 rounded-md bg-amber-50 px-2 py-1 text-amber-900 text-xs tabular-nums dark:bg-amber-950 dark:text-amber-200"
            >
              Allocated{' '}
              {sumMoney(allocations.filter((a) => isMoney(a.amount)).map((a) => a.amount))} of{' '}
              {toMoney(amount)}.{' '}
              {compareMoney(remaining, '0') > 0
                ? `${remaining} left to allocate`
                : `${toMoney(remaining).replace('-', '')} too much`}
            </span>
          ) : flash !== 'hidden' ? (
            <span
              role="status"
              className={cn(
                'inline-flex items-center gap-2 rounded-md bg-emerald-50 px-2 py-1 text-emerald-900 text-xs tabular-nums transition-opacity duration-700 dark:bg-emerald-950 dark:text-emerald-200',
                flash === 'fading' && 'opacity-0',
              )}
            >
              Allocated {toMoney(amount)} of {toMoney(amount)}
            </span>
          ) : null}
        </td>
        <td className="px-1 pt-1 pb-2">
          {editable ? (
            <Button
              variant="link"
              size="sm"
              className="h-auto px-0"
              onClick={() => onChange(addSplitRow(allocations, amount))}
            >
              <Plus className="size-3.5" />
              Add account
            </Button>
          ) : null}
        </td>
        <td />
      </tr>
    </>
  );
}

/**
 * Shown for a few seconds after `key` changes into a complete state, then
 * fading, then hidden — never on mount, so a split that loads already balanced
 * stays quiet.
 */
function useCompletedFlash(complete: boolean, key: string): 'shown' | 'fading' | 'hidden' {
  const [phase, setPhase] = useState<'shown' | 'fading' | 'hidden'>('hidden');
  const firstKey = useRef(key);
  useEffect(() => {
    if (key === firstKey.current || !complete) {
      setPhase('hidden');
      return;
    }
    setPhase('shown');
    const fade = setTimeout(() => setPhase('fading'), ALLOCATED_MESSAGE_MS);
    const hide = setTimeout(() => setPhase('hidden'), ALLOCATED_MESSAGE_MS + ALLOCATED_FADE_MS);
    return () => {
      clearTimeout(fade);
      clearTimeout(hide);
    };
  }, [complete, key]);
  return phase;
}
